import type { PartyContact } from '../components/common/usePartyContact';
import { date as fmtDate, money } from './format';

/**
 * Builds `wa.me` and `sms:` links so a message can be sent from the admin's own WhatsApp or SMS
 * app.
 *
 * Deliberately requires no Meta account, no access token and no approved templates: these are
 * ordinary links that open the installed app with the text already typed in. The trade-off is that
 * they are NOT automatic — a person still presses Send — which is exactly what lets them work with
 * zero setup and zero cost. The automatic server-side path (IAccountNotifier on the backend) stays
 * as it is; this sits alongside it for use while Meta approval is pending, or instead of it.
 */

const NATIONAL_SIGNIFICANT_LENGTH = 10; // Pakistani mobile without the trunk 0

/**
 * Digits-only E.164 without the '+', mirroring the backend's PhoneNumberFormatter. Returns null
 * when the input cannot be read confidently, rather than guessing — the same rule the server uses,
 * so we never open a chat with a number we effectively invented.
 */
export function normalizePhone(raw: string | null | undefined, countryCode = '92'): string | null {
  if (!raw) return null;

  let digits = raw.replace(/\D/g, '');
  if (!digits) return null;

  // International prefix written as 00 (e.g. 0092300...)
  if (digits.startsWith('00')) digits = digits.slice(2);

  // Already fully qualified: 92 + 10 digits
  if (digits.startsWith(countryCode) && digits.length === countryCode.length + NATIONAL_SIGNIFICANT_LENGTH) {
    return digits;
  }

  // Local form with trunk prefix: 0300-1234567
  digits = digits.replace(/^0+/, '');
  if (digits.length === NATIONAL_SIGNIFICANT_LENGTH) return countryCode + digits;

  return null;
}

/** Opens WhatsApp with the message pre-filled. Without a number it opens the contact picker. */
export function whatsAppUrl(phone: string | null, message: string): string {
  const text = encodeURIComponent(message);
  return phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
}

/** Opens the device's SMS app with the message pre-filled. iOS wants '&body=', everything else '?body='. */
export function smsUrl(phone: string, message: string): string {
  const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent);
  return `sms:+${phone}${isIos ? '&' : '?'}body=${encodeURIComponent(message)}`;
}

/** Where the recipient logs in to see the full record — derived from wherever this app is served. */
export const loginLink = () => `${window.location.origin}/login`;

// ---------- message templates ----------
//
// Plain text, composed here rather than on the server: these go out through the admin's own apps,
// so there is no Meta template to match and the admin can edit any of it before sending.

const COMPANY = 'Rana Asif Ali Investments';

/** Drops any line whose value is missing, so a blank CNIC never prints as "CNIC: —". */
function lines(...rows: (string | null | false | undefined)[]): string[] {
  return rows.filter((row): row is string => !!row);
}

const dealTypeLabel = (dealType: string) =>
  dealType === 'FixedReturn' ? 'Fixed return' : 'Monthly % based';

/** The account-holder block quoted at the top of every official message. */
function partyBlock(c: PartyContact): string[] {
  return lines(
    `Name: ${c.name}`,
    c.fatherName && `Father's name: ${c.fatherName}`,
    `${c.partyType === 'Investor' ? 'Investor' : 'Dealer'} code: ${c.code}`,
    c.cnic && `CNIC: ${c.cnic}`,
    c.phone && `Mobile: ${c.phone}`,
    `Account type: ${dealTypeLabel(c.dealType)}`,
  );
}

/** The current position block — worded for whichever model the account runs on. */
function positionBlock(c: PartyContact): string[] {
  if (c.dealType === 'FixedReturn') {
    return lines(
      `Total invested: PKR ${money(c.currentPrincipal)}`,
      `Agreed profit: PKR ${money(c.outstandingProfit)}`,
      `Received so far: PKR ${money(c.paidProfit)}`,
      c.fixedRemaining !== null && `Remaining to receive: PKR ${money(c.fixedRemaining)}`,
    );
  }

  return lines(
    `Current investment: PKR ${money(c.currentPrincipal)}`,
    `Profit paid to date: PKR ${money(c.paidProfit)}`,
    `Outstanding profit: PKR ${money(c.outstandingProfit)}`,
  );
}

export function accountOpenedMessage(c: PartyContact): string {
  return [
    `Assalam-o-Alaikum ${c.name},`,
    '',
    'Your account has been opened successfully. The details on record are:',
    '',
    ...partyBlock(c),
    ...lines(c.memberSince && `Opened on: ${fmtDate(c.memberSince)}`),
    '',
    'ACCOUNT POSITION',
    ...positionBlock(c),
    '',
    'You can log in any time to view your statement, investments and profit:',
    loginLink(),
    '',
    'Your password is handed over at the office — it is deliberately never sent by message.',
    'Please contact us if any detail above is incorrect.',
    '',
    COMPANY,
  ].join('\n');
}

export function transactionMessage(
  c: PartyContact,
  eventLabel: string,
  amount: number,
  when: string,
): string {
  return [
    `Assalam-o-Alaikum ${c.name},`,
    '',
    `A transaction has been recorded on your account.`,
    '',
    'TRANSACTION',
    `Type: ${eventLabel}`,
    `Amount: PKR ${money(amount)}`,
    `Date: ${fmtDate(when)}`,
    '',
    'ACCOUNT DETAILS',
    ...partyBlock(c),
    '',
    'ACCOUNT POSITION',
    ...positionBlock(c),
    '',
    'View the full statement by logging in:',
    loginLink(),
    '',
    'Please contact us if this transaction does not look correct.',
    '',
    COMPANY,
  ].join('\n');
}
